//@ts-nocheck
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import { rounded_rect } from '../../../utils/graphUtils'
import { PowerPerformanceForms } from '../../../types/mutual-fund-detail.types'
import { convertEpochToDate } from '../../../utils/utils'
import { lightImpactVibrate } from '../../../utils/appBridge'

type PropTypes = {
  data: {
    month: number
    value: number
    label: string
    form: `${PowerPerformanceForms}`
    isVisible: boolean
  }[]
  graphForm: `${PowerPerformanceForms}`
  showLabels: boolean
  onInteraction?: Function
  /** Period tab label for accessible chart description (Details_B15). */
  periodLabel?: string
}

const formMetaData = {
  [PowerPerformanceForms.IN_FORM]: {
    label: 'In-form',
    color: '#CBF7D7',
    textColor: '#00661D',
    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <path fillRule="evenodd" clipRule="evenodd" d="M9.98811 4.91837L6.00001 1.5L2.01192 4.91837C1.69743 5.18793 1.661 5.66141 1.93057 5.9759C2.20014 6.2904 2.67361 6.32682 2.98811 6.05725L6.00001 3.47562L9.01192 6.05725C9.32641 6.32682 9.79989 6.2904 10.0695 5.9759C10.339 5.66141 10.3026 5.18793 9.98811 4.91837ZM9.98811 8.91837L6.00001 5.5L2.01192 8.91837C1.69743 9.18793 1.661 9.66141 1.93057 9.9759C2.20014 10.2904 2.67361 10.3268 2.98811 10.0573L6.00001 7.47562L9.01192 10.0573C9.32641 10.3268 9.79989 10.2904 10.0695 9.9759C10.339 9.66141 10.3026 9.18793 9.98811 8.91837Z" fill="#00661D"/>
</svg>`
  },
  [PowerPerformanceForms.ON_TRACK]: {
    label: 'On-track',
    color: '#E4FCEB',
    textColor: '#00661D',
    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <path fillRule="evenodd" clipRule="evenodd" d="M9.51892 5.66837L6.00001 2.25L2.48111 5.66837C2.20361 5.93793 2.17147 6.41141 2.40933 6.7259C2.64718 7.0404 3.06495 7.07682 3.34245 6.80725L5.25 4.9542V9C5.25 9.41421 5.58579 9.75 6 9.75C6.41421 9.75 6.75 9.41421 6.75 9V4.95417L8.65758 6.80725C8.93507 7.07682 9.35285 7.0404 9.5907 6.7259C9.82855 6.41141 9.79642 5.93793 9.51892 5.66837Z" fill="#00661D"/>
</svg>`
  },
  [PowerPerformanceForms.OFF_TRACK]: {
    label: 'Off-track',
    color: '#FFF4E6',
    textColor: '#995400',
    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <path fillRule="evenodd" clipRule="evenodd" d="M2.48108 6.33163L5.99999 9.75L9.51889 6.33163C9.79639 6.06207 9.82853 5.58859 9.59067 5.2741C9.35282 4.9596 8.93505 4.92318 8.65755 5.19275L6.75 7.0458L6.75 3C6.75 2.58579 6.41421 2.25 6 2.25C5.58579 2.25 5.25 2.58579 5.25 3L5.25 7.04582L3.34242 5.19275C3.06493 4.92318 2.64715 4.9596 2.4093 5.2741C2.17145 5.58859 2.20358 6.06207 2.48108 6.33163Z" fill="#995400"/>
</svg>`
  },
  [PowerPerformanceForms.OUT_OF_FORM]: {
    label: 'Out-of-form',
    color: '#FFE9E6',
    textColor: '#992617',
    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <path fillRule="evenodd" clipRule="evenodd" d="M2.01189 7.08163L5.99999 10.5L9.98808 7.08163C10.3026 6.81207 10.339 6.33859 10.0694 6.0241C9.79986 5.7096 9.32639 5.67318 9.01189 5.94275L5.99999 8.52438L2.98808 5.94275C2.67359 5.67318 2.20011 5.7096 1.93054 6.0241C1.66098 6.33859 1.6974 6.81207 2.01189 7.08163ZM2.01189 3.08163L5.99999 6.5L9.98808 3.08163C10.3026 2.81207 10.339 2.33859 10.0694 2.0241C9.79986 1.7096 9.32639 1.67318 9.01189 1.94275L5.99999 4.52438L2.98808 1.94275C2.67359 1.67318 2.20011 1.7096 1.93054 2.0241C1.66098 2.33859 1.6974 2.81207 2.01189 3.08163Z" fill="#992617"/>
</svg>`
  }
}

const getPowerFormMetaData = (form: `${PowerPerformanceForms}`) => {
  return formMetaData[form] ?? { label: 'NA', color: '#F0F4F7', textColor: '#3D3D3D', iconSvg: '' }
}

const getPowerFormLabel = (form: `${PowerPerformanceForms}`) => getPowerFormMetaData(form)?.label
const getPowerFormFromLabel = (label: string) =>
  Object?.keys(formMetaData)?.filter((key) => formMetaData?.[key]?.label === label)?.[0]
const getPowerFormColor = (form: `${PowerPerformanceForms}`) => getPowerFormMetaData(form)?.color
const getPowerFormTextColor = (form: `${PowerPerformanceForms}`) => getPowerFormMetaData(form)?.textColor
const getPowerFormIconSVG = (form: `${PowerPerformanceForms}`) => getPowerFormMetaData(form)?.iconSvg

const HeroGraph = ({ data, showLabels, onInteraction, graphForm, periodLabel }: PropTypes) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const topSVGRef = useRef<SVGSVGElement>(null)
  const lastDataRef = useRef<PropTypes['data'][0]>()
  const [width, setWidth] = useState(window.innerWidth)
  const height = 237
  let path

  useEffect(() => {
    const handleResize = () => {
      const parentWidth = (svgRef.current?.parentNode as HTMLElement)?.clientWidth ?? 0
      const parentHeight = (svgRef.current?.parentNode as HTMLElement)?.clientHeight ?? 250
      setWidth(parentWidth)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const margin = { top: 0, right: 0, bottom: 30, left: 0 }
    const chartWidth = width - margin.left - margin.right
    const chartHeight = height - margin.top - margin.bottom

    const topSVG = d3
      .select(topSVGRef.current)
      .attr('width', chartWidth + margin.left + margin.right)
      .attr('height', 33)
      .attr('aria-hidden', 'true')
      .attr('focusable', 'false')
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const svg = d3
      .select(svgRef.current)
      .attr('width', chartWidth + margin.left + margin.right)
      .attr('height', chartHeight + margin.top + margin.bottom)
      .attr('aria-hidden', 'true')
      .attr('focusable', 'false')
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    let graphStartWidth = 85
    if (data?.length > 7) graphStartWidth = 95
    if (data?.length > 13) graphStartWidth = 100

    const xScale = d3
      .scalePoint()
      .domain(data?.map((d) => d.month))
      .range([graphStartWidth, chartWidth - 15])
      .padding(0.5)

    const minValue = d3.min(data, (d) => d.value)
    const maxValue = d3.max(data, (d) => d.value)
    const lastValue = data?.slice(-1)?.[0]?.value
    if (onInteraction) onInteraction(lastValue, 'Today')

    const yScale = d3.scaleLinear().domain([0, 4]).range([chartHeight, 0])

    const powerUpLogoSvg = `<svg width="55" height="11" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M.87 8V.025h3.576c.642 0 1.18.134 1.615.401.444.26.772.607.986 1.043.221.435.332.913.332 1.432 0 .535-.11 1.024-.332 1.467A2.516 2.516 0 0 1 6.05 5.41c-.443.26-.978.39-1.604.39H2.52V8H.87Zm3.358-3.575c.497 0 .871-.134 1.123-.401s.378-.642.378-1.123-.13-.852-.39-1.111c-.252-.26-.622-.39-1.11-.39H2.52v3.025h1.707Zm6.799 3.712c-.627 0-1.177-.122-1.65-.366a2.71 2.71 0 0 1-1.1-1.054c-.253-.451-.379-.97-.379-1.559 0-.596.126-1.119.379-1.57.26-.45.622-.798 1.088-1.042.474-.245 1.027-.367 1.662-.367.634 0 1.184.122 1.65.367.473.244.836.592 1.088 1.043.26.45.39.974.39 1.57 0 .588-.13 1.107-.39 1.558-.252.45-.615.802-1.088 1.054-.474.244-1.024.366-1.65.366Zm0-1.191c.511 0 .916-.16 1.214-.481.298-.329.447-.764.447-1.307 0-.542-.149-.977-.447-1.306-.298-.328-.703-.493-1.214-.493-.512 0-.917.165-1.215.493-.298.329-.447.764-.447 1.306 0 .543.149.978.447 1.307.298.32.703.48 1.215.48ZM16.296 8 14.37 2.317h1.49l.973 3.15c.123.383.23.868.321 1.456l.012.092h.08c0-.07.023-.256.069-.562.053-.313.133-.642.24-.985l.951-3.128h1.284l.95 3.128c.108.343.184.672.23.985.053.306.08.493.08.562h.08l.012-.092a9.882 9.882 0 0 1 .32-1.455l.975-3.151h1.49L22.001 8h-1.787l-.734-2.59a7.864 7.864 0 0 1-.217-.996 6.362 6.362 0 0 1-.07-.562h-.09c0 .061-.024.248-.07.562a7.864 7.864 0 0 1-.217.996L18.083 8h-1.787Zm7.845-2.853c0-.611.126-1.138.378-1.581.26-.451.626-.795 1.1-1.032.473-.236 1.023-.355 1.65-.355.664 0 1.222.13 1.673.39.45.26.786.615 1.008 1.065.222.443.332.94.332 1.49 0 .13-.007.286-.023.47h-5.362l.779-.63c-.1.664-.008 1.168.275 1.512.29.336.726.504 1.306.504.374 0 .684-.072.928-.218.252-.145.428-.328.527-.55h1.513a2.28 2.28 0 0 1-.951 1.41c-.497.344-1.157.515-1.982.515-.986 0-1.757-.255-2.315-.767-.558-.52-.836-1.26-.836-2.223Zm1.5.023-.744-.539h3.93c0-.39-.134-.71-.401-.962-.267-.26-.653-.39-1.157-.39-.58 0-1.012.176-1.295.527-.283.352-.393.806-.332 1.364ZM31.449 8V2.317h1.42l-.24 1.272h.115c.19-.466.474-.806.848-1.02.382-.214.806-.321 1.272-.321.26 0 .527.034.802.103v1.33a3.679 3.679 0 0 0-.894-.104c-.642 0-1.111.16-1.41.481-.29.314-.435.791-.435 1.433V8h-1.478Zm8.675.137c-1.1 0-1.974-.297-2.623-.893-.642-.596-.963-1.452-.963-2.567V.025h1.662v4.652c0 .688.156 1.207.47 1.558.32.352.805.527 1.455.527.649 0 1.13-.175 1.443-.527.321-.351.481-.87.481-1.558V.025h1.662v4.652c0 1.115-.325 1.97-.974 2.567-.642.596-1.512.893-2.612.893Zm5.097 1.971V8h1.466v2.108H45.22Zm3.506-1.97a2.47 2.47 0 0 1-.997-.195 2.373 2.373 0 0 1-.733-.527 2.146 2.146 0 0 1-.424-.7h-.103L46.686 8H45.22V2.317h1.466L46.457 3.6h.115a2.17 2.17 0 0 1 .779-.997c.382-.283.84-.424 1.375-.424.496 0 .95.134 1.363.401.413.268.738.634.974 1.1.237.466.356.982.356 1.547s-.119 1.07-.356 1.513a2.588 2.588 0 0 1-.974 1.03c-.404.245-.859.367-1.363.367Zm-.413-1.204c.306 0 .58-.068.825-.206a1.57 1.57 0 0 0 .596-.607c.145-.26.218-.558.218-.894 0-.336-.073-.634-.218-.894a1.491 1.491 0 0 0-.596-.596 1.585 1.585 0 0 0-.825-.217c-.305 0-.58.072-.825.217a1.46 1.46 0 0 0-.561.596c-.138.26-.206.558-.206.894 0 .336.068.634.206.894.137.26.325.462.561.607.245.138.52.206.825.206Z" fill="#D1D6D6"/>
  <circle cx="53.5" cy="1.5" r="1.5" fill="#DDDFDF"/>
</svg>
`
    svg
      .append('g')
      .attr('transform', `translate(15, ${chartHeight + 10})`)
      .html(powerUpLogoSvg)

    const colorRanges = [
      getPowerFormColor(PowerPerformanceForms.OUT_OF_FORM),
      getPowerFormColor(PowerPerformanceForms.OFF_TRACK),
      getPowerFormColor(PowerPerformanceForms.ON_TRACK),
      getPowerFormColor(PowerPerformanceForms.IN_FORM)
    ]
    colorRanges.reverse()
    const numOfVerticalSections = colorRanges.length
    const sectionHeight = chartHeight / numOfVerticalSections

    colorRanges.forEach((color, i) => {
      svg
        .append('path')
        .attr('d', (d) => {
          return rounded_rect(0, i * sectionHeight, chartWidth - 1, sectionHeight, 11.5, i == 0, i == 0, false, false)
        })
        .attr('fill', color)
    })

    const numSections = 7
    const sectionedData = Array.from({ length: 7 }, (_, i) => data[Math.floor((i * (data.length - 1)) / 6)])

    svg
      .selectAll('line.vertical-line')
      .data(sectionedData)
      .enter()
      .append('line')
      .attr('class', 'vertical-line')
      .attr('x1', (d) => xScale(d.month))
      .attr('x2', (d) => xScale(d.month))
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', 'white')
      .attr('opacity', 0.5)
      .attr('stroke-width', 1)

    svg
      .append('g')
      .attr('transform', `translate(0,${chartHeight})`)
      .style('font-family', 'Matter')
      .call(
        d3
          .axisBottom(xScale)
          .tickSize(0)
          .tickPadding(10)
          .tickValues(sectionedData.map((d) => d.month))
          .tickFormat((d) => convertEpochToDate(d, '%b'))
      )
      .selectAll('text')
      .attr('aria-hidden', 'true')
      .attr('role', 'presentation')
      .attr('font-weight', (d, i, nodes) => (i === sectionedData.length - 1 ? 700 : 400))
      .attr('font-size', '11px')
      .attr('fill', (d, i, nodes) => (i === sectionedData.length - 1 ? '#000000' : '#6A6E6E'))
      .select('.domain')
      .remove()

    svg.select('.domain').remove()

    const yLabels = [
      getPowerFormLabel(PowerPerformanceForms.OUT_OF_FORM),
      getPowerFormLabel(PowerPerformanceForms.OFF_TRACK),
      getPowerFormLabel(PowerPerformanceForms.ON_TRACK),
      getPowerFormLabel(PowerPerformanceForms.IN_FORM)
    ]

    svg
      .selectAll('.y-label')
      .data(yLabels)
      .enter()
      .append('foreignObject')
      .attr('class', 'y-label')
      .attr('aria-hidden', 'true')
      .attr('role', 'presentation')
      .attr('x', 10)
      .attr('y', (d, i) => yScale(i + 0.65))
      .attr('width', chartWidth - 10)
      .attr('height', 20)
      .html(
        (d) => `<div aria-hidden="true" style="color: ${getPowerFormTextColor(getPowerFormFromLabel(d))}; font-size: 11px; 
        font-weight: ${graphForm === getPowerFormFromLabel(d) ? 700 : 500}; 
        display: flex; gap: 4px;">
      <div style="padding-top: 2px;"> 
      ${getPowerFormIconSVG(getPowerFormFromLabel(d))}   </div>
      <div> ${d} </div> 
      </div>`
      )

    const line = d3
      .line()
      .x((d) => xScale(d.month))
      .y((d) => yScale(d.value))

    const filteredData = data?.filter((d) => d?.isVisible)

    svg
      .append('path')
      .datum(filteredData)
      .attr('fill', 'none')
      .attr('stroke', '#3B4D40')
      .attr('stroke-width', 2)
      .attr('d', line)

    const topDragLine = topSVG
      .append('line')
      .attr('stroke', '#000000')
      .attr('stroke-width', 1.5)
      .attr('y1', 0)
      .attr('y2', 8)
      .style('visibility', 'hidden')

    const dragLine = svg
      .append('line')
      .attr('stroke', '#000000')
      .attr('stroke-width', 1.5)
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .style('visibility', 'hidden')

    const monthTooltip = topSVG
      .append('g')
      .style('visibility', 'hidden')
      .append('rect')
      .attr('width', 121)
      .attr('height', 24)
      .attr('rx', 8)
      .attr('ry', 8)
      .attr('fill', '#D8E2F9')
      .attr('y', -20)

    const monthText = topSVG
      .append('g')
      .style('visibility', 'hidden')
      .append('foreignObject')
      .attr('class', 'y-label')
      .attr('aria-hidden', 'true')
      .attr('role', 'presentation')
      .attr('x', 50)
      .attr('y', -15)
      .attr('width', chartWidth - 10)
      .attr('height', 20)

    const circles = svg
      .selectAll('.y-point')
      .data(filteredData)
      .enter()
      .append('circle')
      .attr('class', '.y-point')
      .attr('cx', (d) => xScale(d.month))
      .attr('cy', (d) => yScale(d.value))
      .attr('r', (_, i) => (i === filteredData?.length - 1 ? 6 : 4))
      .attr('fill', (d, i) => '#3B4D40')
      .attr('stroke', '#FFFFFF')
      .attr('stroke-width', (_, i) => (i === filteredData?.length - 1 ? 3 : 1))

    const TOOLTIP_FLIP_HEIGHT = 25

    const offsetForYScale = (yValue) => {
      const scaleY = yScale(yValue)
      const offset = scaleY < TOOLTIP_FLIP_HEIGHT ? 27 : -10
      return yScale(yValue) + offset
    }

    const getTrianglePath = (yValue) => {
      const scaleY = yScale(yValue)
      const triangleHeight = 5
      const triangleWidth = scaleY < TOOLTIP_FLIP_HEIGHT ? 8 : 10
      const centerX = 0
      return scaleY < TOOLTIP_FLIP_HEIGHT
        ? `M${centerX - triangleWidth / 2},${triangleHeight} L${centerX},0 L${centerX + triangleWidth / 2},${triangleHeight} Z`
        : `M${centerX - triangleWidth / 2}, 0 L${centerX},${triangleHeight} L${centerX + triangleWidth / 2}, 0 Z`
    }

    const getTriangleOffset = (yValue) => {
      const scaleY = yScale(yValue)
      return scaleY < TOOLTIP_FLIP_HEIGHT ? -20 : -2
    }

    const filteredSectionedData = sectionedData?.filter((d) => d?.isVisible)

    const isDisplayInitialTooltip = (d: PropTypes['data'][0]) => {
      return (
        filteredSectionedData?.filter((datum) => datum?.month === d?.month && datum?.value === d?.value)?.length > 0
      )
    }

    const initialTooltips = svg
      .selectAll('.tooltip-initial')
      .data(filteredData)
      .enter()
      .append('g')
      .attr('class', 'tooltip-initial')
      .attr('transform', (d) => `translate(${xScale(d.month)},${offsetForYScale(d.value)})`)
      .style('opacity', (d) => (isDisplayInitialTooltip(d) ? 1 : 0))

    initialTooltips
      .append('text')
      .attr('aria-hidden', 'true')
      .attr('role', 'presentation')
      .attr('x', 0)
      .attr('y', -8)
      .attr('dy', '.35em')
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', 'bold')
      .attr('fill', '#3B4D40')
      .text((d) => d.label)

    initialTooltips
      .insert('rect', 'text')
      .attr('x', function () {
        const textWidth = this.nextSibling.getBBox().width
        return -(textWidth / 2) - 5
      })
      .attr('y', -16)
      .attr('width', function () {
        const textWidth = this.nextSibling.getBBox().width
        return textWidth + 10
      })
      .attr('height', 16)
      .attr('fill', 'white')
      .attr('rx', 5)
      .attr('ry', 5)

    initialTooltips
      .append('path')
      .attr('d', (d) => {
        return getTrianglePath(d.value)
      })
      .attr('fill', 'white')
      .attr('transform', (d) => `translate(0, ${getTriangleOffset(d.value)})`)

    if (!showLabels) initialTooltips.remove()

    svg.on('touchstart touchmove', (event) => {
      // event.preventDefault()
      const touch = event.touches[0]
      const touchX = touch.clientX - (svgRef?.current?.getBoundingClientRect().left ?? 0)

      const closestData = data.reduce((a, b) =>
        Math.abs(xScale(a.month) - touchX) < Math.abs(xScale(b.month) - touchX) ? a : b
      )

      const closestDataX = xScale(closestData.month)

      if (closestData?.isVisible) {
        if (closestData?.month !== lastDataRef?.current?.month) {
          lightImpactVibrate()
          lastDataRef.current = closestData
        }

        dragLine
          .attr('x1', closestDataX)
          .attr('x2', closestDataX)
          .attr('y1', 0)
          .attr('y2', chartHeight)
          .style('visibility', 'visible')

        topDragLine
          .attr('x1', closestDataX + 0.5)
          .attr('x2', closestDataX + 0.5)
          .attr('y1', 4)
          .attr('y2', 34)
          .style('visibility', 'visible')

        circles
          .attr('stroke', '#FFFFFF')
          .attr('stroke-width', (d, i) => (d.month === closestData.month ? 3 : 1))
          .attr('r', (d, i) => (d.month === closestData.month ? 6 : 4))

        initialTooltips.style('opacity', (d) => (d.month === closestData.month ? 1 : 0))

        const closestDataForm = closestData?.form
        const monthYearString = convertEpochToDate(closestData.month, '%b %Y')

        const formLabelId = `form-label-${Date.now()}`
        const seperatorId = `seperator-${Date.now()}`
        const monthYearId = `month-year-${Date.now()}`

        monthText
          .style('visibility', 'visible')
          .html(
            `<div aria-hidden="true" style="font-size: 12px; display: flex; gap: 4px; padding-bottom: 4px;">
           <div id=${formLabelId} style="font-weight: 700">${getPowerFormLabel(closestDataForm)}</div>
           <div id=${seperatorId} style="color: rgba(0, 0, 0, 0.30)"> | </div> 
           <div id=${monthYearId}>${monthYearString} </div>
           </div>`
          )
          .attr('y', 2)
          .attr('aria-hidden', 'true')

        const formLabel = document.getElementById(formLabelId)?.clientWidth
        const seperator = document.getElementById(seperatorId)?.clientWidth
        const monthYear = document.getElementById(monthYearId)?.clientWidth
        const totalWidth = formLabel + seperator + monthYear
        const boxWidth = totalWidth + 30

        let offset = 0
        if (closestDataX > chartWidth * 0.86) offset = boxWidth / 2 - 15
        else if (closestDataX > chartWidth * 0.8) offset = boxWidth / 2 - 40

        monthText.attr('x', closestDataX - totalWidth / 2 - offset - 5)

        monthTooltip
          .style('visibility', 'visible')
          .attr('x', closestDataX - boxWidth / 2 - offset)
          .attr('y', 0)
          .attr('fill', getPowerFormColor(closestData?.form))
          .attr('width', boxWidth)

        if (onInteraction && closestData?.isVisible) onInteraction(closestData.value, closestData.month)
      }
    })

    svg.on('touchend', () => {
      dragLine.style('visibility', 'hidden')
      topDragLine.style('visibility', 'hidden')
      initialTooltips.style('opacity', (d) => (isDisplayInitialTooltip(d) ? 1 : 0))

      monthTooltip.style('visibility', 'hidden')
      monthText.style('visibility', 'hidden')

      circles
        .attr('stroke', '#FFFFFF')
        .attr('stroke-width', (d, i) => (i === filteredData.length - 1 ? 3 : 1))
        .attr('r', (d, i) => (i === filteredData.length - 1 ? 6 : 4))

      if (onInteraction) onInteraction(lastValue, 'Today')
    })

    // iOS VoiceOver still reads SVG <text>/foreignObject under role="img"; hide the whole
    // chart in HTML and expose chartLabel via a sibling (Details_B15 pattern).
    d3.select(svgRef.current).attr('aria-hidden', 'true').attr('focusable', 'false')
    d3.select(topSVGRef.current).attr('aria-hidden', 'true').attr('focusable', 'false')
    d3.select(svgRef.current)
      .selectAll('foreignObject, text, tspan')
      .attr('aria-hidden', 'true')
      .attr('role', 'presentation')
    d3.select(topSVGRef.current)
      .selectAll('foreignObject, text, tspan')
      .attr('aria-hidden', 'true')
      .attr('role', 'presentation')

    return () => {
      d3.select(svgRef.current).selectAll('*').remove()
      d3.select(topSVGRef.current).selectAll('*').remove()
    }
  }, [data, width, showLabels])

  const chartLabel = `Past trends graph for ${periodLabel ?? 'selected period'}, fund performance form ${getPowerFormLabel(graphForm) ?? 'unknown'}`

  return (
    <div className='flex flex-col'>
      <span className='sr-only'>{chartLabel}</span>
      <div className='relative flex flex-col' aria-hidden='true'>
        <div className='absolute bottom-full left-0 right-0 pointer-events-none mb-[-2px]'>
          <svg ref={topSVGRef} aria-hidden='true' focusable='false'></svg>
        </div>
        <div>
          <svg
            style={{ borderRadius: '12px', border: '0.5px solid #8E93933A', overflow: 'visible', position: 'relative' }}
            ref={svgRef}
            aria-hidden='true'
            focusable='false'
          ></svg>
        </div>
      </div>
    </div>
  )
}

export default HeroGraph
